// Accrual detail: the release timetable with a record-next action that
// posts the next period, plus per-period undo for recorded entries (an
// exact mirror entry, never an edit). Both are financial mutations, so each
// is confirmed through the shared Dialog before its API call runs.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  type AccrualEntry,
  type AccrualSchedule,
  getAccrualSchedule,
  recognizeAccrualEntry,
  reverseAccrualEntry,
} from "@/lib/api";
import { useAccountLabels } from "@/lib/labels";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "active":
      return "info";
    case "cancelled":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function nextUnpostedEntry(schedule: AccrualSchedule): AccrualEntry | undefined {
  return [...schedule.entries]
    .filter((entry) => !entry.posted)
    .sort((a, b) => a.sort_order - b.sort_order)[0];
}

interface PendingReverse {
  entry: AccrualEntry;
  reversalDate: string;
}

export function AccrualDetailPage() {
  const { accrualScheduleId } = useParams({ strict: false }) as {
    accrualScheduleId: string;
  };
  const [schedule, setSchedule] = useState<AccrualSchedule | null>(null);
  // Read failure (the page-level error with Retry). Action failures stay in
  // their own dialog so a failed post never looks like a failed page load.
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRecognize, setConfirmRecognize] = useState(false);
  const [recognizeError, setRecognizeError] = useState<string | null>(null);
  const [reversingEntry, setReversingEntry] = useState<AccrualEntry | null>(
    null,
  );
  const [pendingReverse, setPendingReverse] = useState<PendingReverse | null>(
    null,
  );
  const [reversing, setReversing] = useState(false);
  const [reverseError, setReverseError] = useState<string | null>(null);
  const accountLabels = useAccountLabels();

  const reload = useCallback(() => {
    if (!accrualScheduleId) return;
    setError(null);
    getAccrualSchedule(accrualScheduleId)
      .then(setSchedule)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [accrualScheduleId]);

  useEffect(reload, [reload]);

  const nextEntry = schedule ? nextUnpostedEntry(schedule) : undefined;
  const canRecognize =
    schedule !== null &&
    schedule.status.toLowerCase() === "active" &&
    nextEntry !== undefined;

  const recognize = async () => {
    if (!accrualScheduleId) return;
    setBusy(true);
    setRecognizeError(null);
    setNotice(null);
    try {
      const result = await recognizeAccrualEntry(accrualScheduleId);
      setNotice(`Recorded the next period (entry ${result.journal_entry_id}).`);
      setConfirmRecognize(false);
      reload();
    } catch (cause) {
      setRecognizeError(`Record failed: ${toErrorMessage(cause)}`);
    } finally {
      setBusy(false);
    }
  };

  const confirmReverse = async () => {
    if (!pendingReverse || !schedule) return;
    setReversing(true);
    setReverseError(null);
    try {
      const result = await reverseAccrualEntry(
        schedule.id,
        pendingReverse.entry.id,
        { reversal_date: pendingReverse.reversalDate },
      );
      setNotice(`Undone with mirror entry ${result.reversal_journal_entry_id}.`);
      setPendingReverse(null);
      setReversingEntry(null);
      reload();
    } catch (cause) {
      setReverseError(toErrorMessage(cause));
    } finally {
      setReversing(false);
    }
  };

  const recorded = schedule
    ? schedule.entries
        .filter((entry) => entry.posted)
        .reduce((sum, entry) => sum + entry.amount_minor, 0)
    : 0;

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/accruals"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Accruals
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {schedule?.description ?? accrualScheduleId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {schedule === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : schedule ? (
        <>
          <PageHeader
            title={schedule.description}
            description={
              <>
                {new Date(schedule.start_date).toLocaleDateString()} →{" "}
                {new Date(schedule.end_date).toLocaleDateString()} ·{" "}
                <StatusDot
                  tone={statusTone(schedule.status)}
                  label={plainStatus(schedule.status)}
                />
              </>
            }
            actions={
              canRecognize ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setRecognizeError(null);
                    setConfirmRecognize(true);
                  }}
                  disabled={busy}
                  title="Post the next period (debits the expense account, credits the accrual account)"
                >
                  {busy ? "Recording…" : "Record next period"}
                </Button>
              ) : null
            }
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Total
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: schedule.total_minor, currency: "USD" })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Recorded so far
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: recorded, currency: "USD" })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Accounts
              </p>
              <div className="mt-1 grid gap-1 text-sm">
                <span>
                  Expense:{" "}
                  {accountLabels.get(schedule.account_id) ?? (
                    <span title={schedule.account_id}>Unknown account</span>
                  )}
                </span>
                <span>
                  Accrual:{" "}
                  {accountLabels.get(schedule.reverse_account_id) ?? (
                    <span title={schedule.reverse_account_id}>
                      Unknown account
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Undo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedule.entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        This accrual has no periods yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    schedule.entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="whitespace-nowrap">
                          {new Date(entry.period_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {entry.posted ? (
                            entry.journal_entry_id ? (
                              <Link
                                to="/journal/$entryId"
                                params={{ entryId: entry.journal_entry_id }}
                                className="underline-offset-4 hover:underline"
                                title={entry.journal_entry_id}
                              >
                                <StatusDot tone="success" label="Recorded" />
                              </Link>
                            ) : (
                              <StatusDot tone="success" label="Recorded" />
                            )
                          ) : (
                            <StatusDot tone="idle" label="Upcoming" />
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney({
                            cents: entry.amount_minor,
                            currency: "USD",
                          })}
                        </TableCell>
                        <TableCell className="text-right">
                          {entry.posted ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setReverseError(null);
                                setReversingEntry(entry);
                              }}
                              title="Undo with an exact mirror entry (reversal)"
                            >
                              Undo
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="stack-list grid gap-3 min-[64rem]:hidden">
            {schedule.entries.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                This accrual has no periods yet.
              </p>
            ) : (
              schedule.entries.map((entry) => (
                <article
                  key={entry.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title">
                      {new Date(entry.period_date).toLocaleDateString()}
                    </span>
                    <span className="tabular-nums">
                      {formatMoney({
                        cents: entry.amount_minor,
                        currency: "USD",
                      })}
                    </span>
                  </div>
                  <CardField label="Status">
                    {entry.posted ? (
                      entry.journal_entry_id ? (
                        <Link
                          to="/journal/$entryId"
                          params={{ entryId: entry.journal_entry_id }}
                          className="underline-offset-4 hover:underline"
                          title={entry.journal_entry_id}
                        >
                          <StatusDot tone="success" label="Recorded" />
                        </Link>
                      ) : (
                        <StatusDot tone="success" label="Recorded" />
                      )
                    ) : (
                      <StatusDot tone="idle" label="Upcoming" />
                    )}
                  </CardField>
                  <CardField label="Amount">
                    {formatMoney({
                      cents: entry.amount_minor,
                      currency: "USD",
                    })}
                  </CardField>
                  {entry.posted ? (
                    <div className="flex flex-wrap gap-2 pt-3">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setReverseError(null);
                          setReversingEntry(entry);
                        }}
                        title="Undo with an exact mirror entry (reversal)"
                      >
                        Undo
                      </Button>
                    </div>
                  ) : null}
                </article>
              ))
            )}
          </div>
        </>
      ) : null}

      {reversingEntry ? (
        <ReverseEntryDrawer
          entry={reversingEntry}
          busy={reversing}
          onClose={() => {
            if (reversing) return;
            setReversingEntry(null);
            setPendingReverse(null);
            setReverseError(null);
          }}
          onReview={(reversalDate) => {
            setReverseError(null);
            setPendingReverse({ entry: reversingEntry, reversalDate });
          }}
        />
      ) : null}

      <Dialog
        open={confirmRecognize}
        onOpenChange={(open) => {
          if (!open && !busy) {
            setConfirmRecognize(false);
            setRecognizeError(null);
          }
        }}
        title="Record this period?"
        description={
          nextEntry
            ? `Posts the ${new Date(nextEntry.period_date).toLocaleDateString()} period for ${formatMoney({ cents: nextEntry.amount_minor, currency: "USD" })} to the books now — debits the expense account and credits the accrual account. A posted journal can be undone with a mirror entry, never edited.`
            : undefined
        }
      >
        {recognizeError ? (
          <p role="alert" className="mb-3 text-sm text-[var(--danger)]">
            {recognizeError}
          </p>
        ) : null}
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setConfirmRecognize(false);
              setRecognizeError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={() => void recognize()}
          >
            {busy ? "Recording…" : "Record period"}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={pendingReverse !== null}
        onOpenChange={(open) => {
          if (!open && !reversing) {
            setPendingReverse(null);
            setReverseError(null);
          }
        }}
        title="Undo this period?"
        description={
          pendingReverse
            ? `Posts an exact mirror of the ${new Date(pendingReverse.entry.period_date).toLocaleDateString()} period (${formatMoney({ cents: pendingReverse.entry.amount_minor, currency: "USD" })}) dated ${new Date(`${pendingReverse.reversalDate}T00:00:00`).toLocaleDateString()}. The original entry stays in history and the period becomes recordable again — this is not an edit.`
            : undefined
        }
      >
        {reverseError ? (
          <p role="alert" className="mb-3 text-sm text-[var(--danger)]">
            {reverseError}
          </p>
        ) : null}
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            disabled={reversing}
            onClick={() => {
              setPendingReverse(null);
              setReverseError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={reversing}
            onClick={() => void confirmReverse()}
          >
            {reversing ? "Undoing…" : "Undo period"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function ReverseEntryDrawer({
  entry,
  busy,
  onClose,
  onReview,
}: {
  entry: AccrualEntry;
  busy: boolean;
  onClose: () => void;
  onReview: (reversalDate: string) => void;
}) {
  const [reversalDate, setReversalDate] = useState(todayInput);
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (!reversalDate) {
      setError("Reversal date is required.");
      return;
    }
    onReview(reversalDate);
  };

  return (
    <Drawer
      title="Undo a recorded period"
      description="Posts an exact mirror entry that cancels the original. The original stays in history; the period becomes recordable again. You will be asked to confirm the entry and date before it posts."
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      className="w-[min(34rem,100%)]"
    >
      <form onSubmit={submit} className="grid gap-3">
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <p className="text-sm text-[var(--muted-foreground)]">
          Undoing the period recorded for{" "}
          {new Date(entry.period_date).toLocaleDateString()} (
          {formatMoney({ cents: entry.amount_minor, currency: "USD" })}).
        </p>
        <label
          htmlFor="accr-reverse-date"
          className="grid gap-1 text-xs font-medium"
        >
          Undo as of
          <Input
            id="accr-reverse-date"
            type="date"
            value={reversalDate}
            onChange={(e) => setReversalDate(e.target.value)}
            required
            disabled={busy}
          />
        </label>
        <Button type="submit" disabled={busy}>
          Review undo
        </Button>
      </form>
    </Drawer>
  );
}
